import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import withLayout from "../layout";
import RentHeader, { RentFooter } from "../rent/RentHeader";
import { createCheckout, getLocation, rememberBooking } from "../rent/api";
import { combine, euro, fmtDate, fmtDateTime, isNightType, isSubscription, quote, suggestNow, toDateInput, toTimeInput } from "../rent/tariffs";
import { Icons, tariffName, tariffWhen, WeekdayPicker, weekdayRange } from "../rent/ui";
import "../login/login.css";

// Kennzeichen im API-Format "ORTSKENNUNG-REST" (z. B. "LÖ-AB123")
export const buildPlate = (value, country) => {
  const input = value.trim().toUpperCase();
  if (country === "de") {
    const match = input.match(/^([A-ZÄÖÜ]{1,3})[\s-]+([A-ZÄÖÜ0-9][A-ZÄÖÜ0-9\s-]*)$/);
    if (!match) return null;
    const rest = match[2].replace(/[\s-]/g, "");
    if (rest.length < 2 || rest.length > 7) return null;
    return `${match[1]}-${rest}`;
  }
  const foreign = input.replace(/[\s-]/g, "");
  return /^[A-Z0-9]{5,12}$/.test(foreign) ? foreign : null;
};

const nextFullHour = () => {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d;
};

// Startwerte aus Suche bzw. QR-Einstieg
const initialSelection = (location, tariff, query) => {
  const now = query.get("now") === "1";
  const from = query.get("parkingFrom") ? new Date(query.get("parkingFrom")) : null;
  const until = query.get("parkingUntil") ? new Date(query.get("parkingUntil")) : null;
  const hint = now ? suggestNow(location) : null;
  const sel = {};
  const start = now ? new Date() : from && !isNaN(from) ? from : nextFullHour();
  const end = now ? new Date(Date.now() + 2 * 36e5) : until && !isNaN(until) ? until : new Date(start.getTime() + 2 * 36e5);
  sel.fromDate = toDateInput(start);
  sel.fromTime = toTimeInput(start);
  sel.untilDate = toDateInput(end);
  sel.untilTime = toTimeInput(end);
  sel.date = hint && hint.date ? hint.date : toDateInput(from && !isNaN(from) ? from : new Date());
  sel.count = 1;
  sel.weekdays = tariff && tariff.weekdays ? [...tariff.weekdays] : [1, 2, 3, 4, 5];
  return sel;
};

const BookingPage = ({ setLoaderBox }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { id, tariffId } = useParams();
  const navigate = useNavigate();
  const [query] = useSearchParams();
  const [loc, setLoc] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [sel, setSel] = useState(null);
  const [country, setCountry] = useState("de");
  const [plate, setPlate] = useState(query.get("plate") || "");
  const [email, setEmail] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    document.title = "Buchen | innopark24";
    let alive = true;
    setLoaderBox(true);
    getLocation(id, { activeTab: query.get("activeTab") || "parking-hourly" })
      .then((l) => {
        if (!alive) return;
        setLoc(l);
        setSel(initialSelection(l, l.tariffs.find((x) => x.id === tariffId), query));
      })
      .catch(() => alive && setLoadError(true))
      .finally(() => alive && setLoaderBox(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const tariff = loc ? loc.tariffs.find((x) => x.id === tariffId) || loc.tariffs[0] : null;
  const set = (patch) => setSel((s) => ({ ...s, ...patch }));

  const q = useMemo(() => {
    if (!tariff || !sel) return { ok: false };
    if (tariff.type === "hour") return quote(tariff, { from: combine(sel.fromDate, sel.fromTime), until: combine(sel.untilDate, sel.untilTime) });
    return quote(tariff, { date: sel.date, count: sel.count, weekdays: sel.weekdays });
  }, [tariff, sel]);

  const pickTariff = (x) => {
    const params = new URLSearchParams(query);
    params.delete("now");
    navigate(`/buchen/${encodeURIComponent(id)}/${encodeURIComponent(x.id)}?${params.toString()}`, { replace: true });
    if (x.weekdays) set({ weekdays: [...x.weekdays] });
  };

  const sitePath = `/p/${encodeURIComponent(id)}${query.get("from") ? `?from=${encodeURIComponent(query.get("from"))}` : ""}`;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    const licensePlate = buildPlate(plate, country);
    const mailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    const daysOk = tariff.type !== "workday" || sel.weekdays.length > 0;
    setErrors({ plate: !licensePlate, email: !mailOk, period: !q.ok, days: !daysOk });
    if (!licensePlate || !mailOk || !q.ok || !daysOk || !agreed) return;

    const body = {
      parkingId: loc.id,
      tariffId: tariff.id,
      tariffType: tariff.type,
      recurring: isSubscription(tariff),
      start: q.start.toISOString(),
      end: q.end ? q.end.toISOString() : null,
      quantity: q.count || q.hours || 1,
      weekdays: tariff.type === "workday" ? sel.weekdays : undefined,
      expectedTotal: q.total,
      licensePlate,
      email: email.trim(),
      lang,
      successUrl: `${window.location.origin}/erfolg?session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: window.location.href,
      // Felder der bisherigen API
      activeTab: isSubscription(tariff) ? "parking-monthly" : "parking-hourly",
      parkingFrom: q.start.toISOString(),
      parkingUntil: q.end ? q.end.toISOString() : undefined,
      duration: tariff.type === "workday" ? "Mon-Fri" : tariff.type === "full" ? "Seven Days Per Week" : undefined,
    };
    rememberBooking({
      title: loc.title,
      code: loc.code || loc.id,
      tariff: tariffName(tariff, t),
      type: tariff.type,
      start: body.start,
      end: body.end,
      total: q.total,
      monthly: q.monthly,
      plate: licensePlate,
      email: body.email,
      weekdays: body.weekdays,
    });
    setLoaderBox(true);
    try {
      const url = await createCheckout(body);
      window.location.href = url;
      return;
    } catch (err) {
      setSubmitError(t("rpPayError"));
    }
    setLoaderBox(false);
  };

  if (loadError) {
    return (
      <div className="rp">
        <RentHeader />
        <main className="rp-main">
          <div className="rx-card rx-done" style={{ borderTopColor: "var(--ipl-blue)" }}>
            <h1>{t("rxNotFoundHead")}</h1>
            <p className="rx-muted">{t("rxNotFoundText", { code: id })}</p>
            <div className="rx-actions" style={{ marginTop: 18 }}>
              <Link className="ipl-btn" to="/">
                <span>{t("rxBackStart")}</span>
              </Link>
            </div>
          </div>
        </main>
        <RentFooter />
      </div>
    );
  }

  const field = (label, input, key) => (
    <div className={`ipl-field ${key && errors[key] ? "has-error" : ""}`}>
      <label>{label}</label>
      {input}
    </div>
  );
  const dateInput = (value, onChange, min) => (
    <div className="ipl-input">
      <input type="date" value={value} min={min} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
  const countSelect = (options) => (
    <div className="ipl-input">
      <select value={sel.count} onChange={(e) => set({ count: Number(e.target.value) })}>
        {options.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
    </div>
  );

  let period = null;
  if (tariff && sel) {
    const today = toDateInput(new Date());
    if (tariff.type === "hour") {
      period = (
        <>
          <div className="ipl-two">
            {field(t("rxFrom"), dateInput(sel.fromDate, (v) => set({ fromDate: v }), today))}
            {field(t("rxTime"), <div className="ipl-input"><input type="time" value={sel.fromTime} onChange={(e) => set({ fromTime: e.target.value })} /></div>)}
          </div>
          <div className="ipl-two">
            {field(t("rxUntil"), dateInput(sel.untilDate, (v) => set({ untilDate: v }), sel.fromDate), "period")}
            {field(t("rxTime"), <div className="ipl-input"><input type="time" value={sel.untilTime} onChange={(e) => set({ untilTime: e.target.value })} /></div>, "period")}
          </div>
          {q.ok ? (
            <p className="ipl-hint">{t("rxHourHint", { hours: q.hours })}</p>
          ) : (
            <p className="ipl-hint" style={{ color: "var(--ipl-red)" }}>{t("rpErrUntil")}</p>
          )}
        </>
      );
    } else if (tariff.type === "day") {
      period = (
        <>
          <div className="ipl-two">
            {field(t("rxFirstDay"), dateInput(sel.date, (v) => set({ date: v }), today))}
            {field(t("rxDays"), countSelect([1, 2, 3, 4, 5, 6, 7, 14]))}
          </div>
          <p className="ipl-hint">{t("rxDayHint")}</p>
        </>
      );
    } else if (tariff.type === "night") {
      period = (
        <>
          <div className="rx-fixed">
            {Icons.night}
            <div>
              <b>{tariffWhen(tariff, t, lang)}</b>
              <span>{t("rxFixedNight")}</span>
            </div>
          </div>
          <div className="ipl-two">
            {field(t("rxNightOf"), dateInput(sel.date, (v) => set({ date: v }), toDateInput(new Date(Date.now() - 864e5))))}
            {field(t("rxNights"), countSelect([1, 2, 3, 4, 5, 6, 7]))}
          </div>
          <p className="ipl-hint">{t("rxNightLate")}</p>
        </>
      );
    } else {
      period = (
        <>
          <div className="rx-fixed">
            {Icons[tariff.type]}
            <div>
              <b>{tariffWhen(tariff, t, lang)}</b>
              <span>{t("rxSubCancel")}</span>
            </div>
          </div>
          {tariff.type === "workday" && (
            <div className={`ipl-field ${errors.days ? "has-error" : ""}`}>
              <label>{t("rxWeekdays")}</label>
              <WeekdayPicker value={sel.weekdays} allowed={tariff.weekdays} lang={lang} onChange={(v) => set({ weekdays: v })} />
              <div className="ipl-err">{t("rxWeekdaysError")}</div>
            </div>
          )}
          {field(t("rxStart"), dateInput(sel.date, (v) => set({ date: v }), today))}
          <p className="ipl-hint">{t("rxSubHint")}</p>
        </>
      );
    }
  }

  const lines = [];
  if (tariff && q.ok) {
    lines.push([t("rxTariff"), tariffName(tariff, t)]);
    if (tariff.type === "hour") {
      lines.push([t("rxPeriod"), `${fmtDateTime(q.start, lang)} – ${fmtDateTime(q.end, lang)}`]);
      lines.push([t("rxDuration"), t("rxHoursN", { count: q.hours })]);
    } else if (isSubscription(tariff)) {
      lines.push([t("rxValid"), tariffWhen(tariff, t, lang)]);
      if (tariff.type === "workday") lines.push([t("rxWeekdays"), weekdayRange(sel.weekdays, lang) || t("rxDaily")]);
      lines.push([t("rxStart"), fmtDate(q.start, lang, { year: "numeric" })]);
      lines.push([t("rxTerm"), t("rxMonthlyCancel")]);
    } else {
      lines.push([t("rxPeriod"), `${fmtDateTime(q.start, lang)} – ${fmtDateTime(q.end, lang)}`]);
      lines.push([tariff.type === "night" ? t("rxNights") : t("rxDays"), `${q.count} × ${euro(tariff.price, lang)}`]);
    }
  }

  return (
    <div className="rp">
      <RentHeader />
      <main className="rp-main">
        <Link className="rx-back" to={sitePath}>
          {Icons.back}
          {t("rxBackSite")}
        </Link>
        <div className="rx-steps">
          <span className="done">✓ {t("rxStepSite")}</span>
          <span className="done">✓ {t("rxStepTariff")}</span>
          <span className="on">3 {t("rxStepDetails")}</span>
          <span>4 {t("rxStepPay")}</span>
        </div>

        {loc && tariff && sel && (
          <form className="rx-book" onSubmit={onSubmit} noValidate>
            <div>
              <section className="rx-card rx-panel">
                <h2>{t("rxPanelPeriod")}</h2>
                {loc.tariffs.length > 1 && (
                  <div className="rx-tsel" role="group" aria-label={t("rxTariff")}>
                    {loc.tariffs.map((x) => (
                      <button type="button" key={x.id} aria-pressed={x.id === tariff.id} onClick={() => pickTariff(x)}>
                        {tariffName(x, t)}
                      </button>
                    ))}
                  </div>
                )}
                {period}
              </section>

              <section className="rx-card rx-panel">
                <h2>{t("rpYourDetails")}</h2>
                <div className={`ipl-field ${errors.plate ? "has-error" : ""}`}>
                  <label htmlFor="rp-plate">{t("licensePlate")}</label>
                  <div className="ipl-plate-row">
                    <div className="ipl-country">
                      <select aria-label={t("rpCountry")} value={country} onChange={(e) => setCountry(e.target.value)}>
                        <option value="de">DE</option>
                        <option value="int">Int</option>
                      </select>
                    </div>
                    <div className="ipl-input">
                      {Icons.car}
                      <input
                        id="rp-plate"
                        type="text"
                        autoComplete="off"
                        autoCapitalize="characters"
                        spellCheck="false"
                        maxLength={14}
                        placeholder={country === "de" ? "M-AB1234" : "AB123CD"}
                        value={plate}
                        onChange={(e) => {
                          setPlate(e.target.value.toUpperCase().replace(/[^A-ZÄÖÜ0-9\s-]/g, ""));
                          setErrors((x) => ({ ...x, plate: false }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="ipl-hint">{t("rxPlateTicket")}</div>
                  <div className="ipl-err">{t(country === "de" ? "rpPlateErrorDe" : "rpPlateErrorInt")}</div>
                </div>

                <div className={`ipl-field ${errors.email ? "has-error" : ""}`}>
                  <label htmlFor="rp-mail">{t("rpEmail")}</label>
                  <div className="ipl-input">
                    {Icons.mail}
                    <input
                      id="rp-mail"
                      type="email"
                      autoComplete="email"
                      placeholder="name@beispiel.de"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setErrors((x) => ({ ...x, email: false }));
                      }}
                    />
                  </div>
                  <div className="ipl-hint">{t("rxEmailHint")}</div>
                  <div className="ipl-err">{t("rpEmailError")}</div>
                </div>

                <label className="ipl-check">
                  <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                  <span dangerouslySetInnerHTML={{ __html: t("rpAgreement") }}></span>
                </label>
              </section>
            </div>

            <aside className="rx-card rx-sum">
              <h2>{t("rxSummary")}</h2>
              <div className="rx-loc-mini">
                <div className="rx-thumb">{loc.images[0] && <img src={loc.images[0]} alt="" />}</div>
                <div>
                  <b>{loc.title}</b>
                  <p>{loc.address}</p>
                </div>
              </div>
              {lines.map(([k, v]) => (
                <div className="rx-line" key={k}>
                  <span>{k}</span>
                  <b>{v}</b>
                </div>
              ))}
              {plate && (
                <div className="rx-line">
                  <span>{t("licensePlate")}</span>
                  <b>{plate}</b>
                </div>
              )}
              <div className="rx-line total">
                <span>{q.monthly ? t("rxMonthly") : t("rxTotal")}</span>
                <b>{q.ok ? `${euro(q.total, lang)}${q.monthly ? ` ${t("rxPerMonthShort")}` : ""}` : "–"}</b>
              </div>
              {tariff.type === "hour" && tariff.maxPerDay && q.ok && q.total < q.hours * tariff.price && <p className="rx-small-note">{t("rxMaxApplied")}</p>}
              {isNightType(tariff) && !isSubscription(tariff) && <p className="rx-small-note">{t("rxNightOnly")}</p>}

              {submitError && (
                <div className="ipl-status" role="alert" style={{ marginTop: 12 }}>
                  {submitError}
                </div>
              )}
              <button className="ipl-btn" type="submit" disabled={!agreed || !q.ok}>
                <span>{isSubscription(tariff) ? t("rxSubscribe") : t("rxPayAmount", { amount: q.ok ? euro(q.total, lang) : "" })}</span>
              </button>
              <div className="rx-pay">
                <span>VISA</span>
                <span>Mastercard</span>
                <span>Apple Pay</span>
                <span>Google Pay</span>
                <span>PayPal</span>
              </div>
              <p className="rx-secure">
                {Icons.lock}
                {t("rxSecureStripe")}
              </p>
            </aside>
          </form>
        )}
      </main>
      <RentFooter />
    </div>
  );
};

export default withLayout(BookingPage);
